/**
 * One call. The server plans, queries and narrates.
 *
 * WHAT THIS REPLACES, AND WHY
 *
 * This used to be three steps: guardrails turned the question into a spec,
 * THIS FILE computed the figures in the browser from generated data, and
 * guardrails wrote prose from the figures the browser handed back.
 *
 * That was the honest arrangement while the numbers were invented — the model
 * still never did arithmetic, which is what kept the chat and the dashboard
 * agreeing. It stops being honest the moment the numbers are real, for two
 * reasons:
 *
 *   - There is nothing in the browser to compute from. The data is in
 *     BigQuery.
 *   - /reporting/narrate wrote an authoritative summary of whatever figures
 *     its caller posted. With generated figures that is meaningless; with real
 *     ones it is a way to get an invented CPA described as fact.
 *
 * Both are closed by doing the whole thing in one request against the
 * warehouse. The model still only plans and narrates: every metric and
 * dimension it names is checked against the client's own metric registry
 * server-side and refused if it does not fit, and every figure is computed by
 * BigQuery from the registry's definition.
 *
 * Requests go through api.js, so they inherit the Firebase token handling, the
 * AuthError type and the `detail` error format.
 */

import { reportingPost } from '../api';

/**
 * Server dimension columns, in this page's vocabulary.
 *
 * A SHIM, AND LABELLED AS ONE.
 *
 * The warehouse names columns after what they are — campaign_name,
 * ad_group_name — and this page was built against the generated data's
 * shorter names. Translating here keeps the whole of AIInsights.js working
 * unchanged against real data, which is a far smaller and more reviewable
 * change than rewriting the rendering at the same time as the data source.
 *
 * It is not a permanent arrangement. When the page is updated to the server's
 * vocabulary this table goes, and anything not in it already passes through
 * untouched — so a new dimension is never silently dropped, it simply arrives
 * under its real name.
 */
const DIMENSION_ALIASES = {
  campaign_name: 'campaign',
  ad_group_name: 'adset',
  ad_name: 'ad',
  account_name: 'account',
};

function pageDimension(column) {
  return DIMENSION_ALIASES[column] || column;
}

/** Rename the aliased dimension keys in a row, leaving metrics alone. */
function toPageRow(row, aliases) {
  if (!aliases.length) return row;
  const out = { ...row };
  aliases.forEach(([column, alias]) => {
    if (column in out) {
      out[alias] = out[column];
      delete out[column];
    }
  });
  return out;
}

/**
 * @param {string}   question  what the user typed
 * @param {string?}  clientId  the selected client, or null. The server decides
 *                             what that resolves to; it is not a hint.
 * @param {Array}    history   prior turns, so follow-ups resolve
 *
 * dataKeys is gone. It used to say which clients' generated rows to add
 * together, and there is no such choice now: each client's data is in its own
 * dataset and the server answers for one client at a time.
 */
export async function askAgent(question, clientId, history = []) {
  const response = await reportingPost('/reporting/ask', {
    question,
    history,
    client_id: clientId,
  });

  const planned = response.plan || {};
  const dimensionColumns = planned.dimensions || [];
  const aliases = dimensionColumns
    .map((c) => [c, pageDimension(c)])
    .filter(([column, alias]) => column !== alias);

  // The page renders one dimension. When the planner asked for more, the first
  // is the one it groups by and the rest stay in the rows for the table.
  const dim = dimensionColumns.length ? pageDimension(dimensionColumns[0]) : null;

  const rows = (response.rows || []).map((r) => toPageRow(r, aliases));
  const compared = Boolean(planned.compare_previous);

  // A comparison comes back as one result set carrying a `period` column -
  // one query, so the two periods cannot disagree about what was in the
  // window. Split here into the two sets the page expects.
  const current = compared ? rows.filter((r) => r.period === 'current') : rows;
  const previous = compared ? rows.filter((r) => r.period === 'previous') : null;

  const campaignFilter = (planned.filters || [])
    .filter((f) => f.dimension === 'campaign_name')
    .flatMap((f) => f.values || []);

  const totals = response.totals || [];
  const currentTotals = compared
    ? totals.find((t) => t.period === 'current')
    : totals[0];
  const previousTotals = compared
    ? totals.find((t) => t.period === 'previous')
    : null;

  return {
    plan: {
      metrics: planned.metrics || [],
      dimension: dim,
      comparePrevious: compared,
      rangeDays: response.window?.days,
      sortMetric: planned.sort_by || null,
      sortDir: planned.sort_dir || 'desc',
      intent: response.intent || '',
      // What was actually read, for the provenance line. The server resolves
      // the grain itself — a long search-term window silently becomes the
      // partitioned view — so this is the warehouse's answer, not the plan's.
      grain: response.grain || planned.grain || null,
      view: response.view || null,
    },
    spec: {
      dimension: dim,
      // The card's provenance line says which campaigns were matched. Carried
      // across from the server's filter list rather than dropped: a filtered
      // answer that does not say it was filtered is the most misleading shape
      // a correct number can take.
      campaignFilter: campaignFilter.length ? campaignFilter : null,
      filters: planned.filters || [],
      limit: planned.limit || null,
      sort: planned.sort_by
        ? { metric: planned.sort_by, dir: planned.sort_dir || 'desc' }
        : null,
      dateRange: { start: response.window?.start, end: response.window?.end },
    },
    result: {
      range: { start: response.window?.start, end: response.window?.end },
      rowCount: current.length,
      results: current,
    },
    comparison: previous ? { results: previous } : null,

    // Computed by the warehouse, not added up from the rows above. A column of
    // CPAs cannot be summed and must not be averaged - each ratio is
    // recomputed from the period's summed numerator and denominator.
    totals: currentTotals || null,
    previousTotals: previousTotals || null,

    answer: response.answer || '',

    // Every column's label, as the client's own registry defines it. The page
    // used a constant written for the generated data, so a client who renames
    // a metric would never have seen it - which is most of the point of a
    // per-client registry. Keyed by column name, dimensions included, with
    // the aliased dimensions keyed the way the page knows them.
    labels: Object.fromEntries(
      (response.columns || []).map((c) => [pageDimension(c.name), c.label]),
    ),

    // What the figures do and do not support: conversions still arriving for
    // recent days, a conversion mapping the client has not confirmed, a grain
    // that is one platform only. Shown rather than dropped - an unconfirmed
    // CPA is indicative, and saying so is the difference between a governance
    // product and a dashboard.
    caveats: response.caveats || [],
    columns: response.columns || [],
    window: response.window || null,
  };
}
